import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import APIService from "../APIService";
import FacultyDashboardShell from "./FacultyDashboardShell";
import "./Dashboard.css";
import { formatLocalDateTime } from "../utils/datetime";
import { getFacultyWorkspaceCourse, setFacultyWorkspaceCourse } from "../utils/facultyWorkspace";

function FacultyInsights() {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [courseFilter, setCourseFilter] = useState(() => getFacultyWorkspaceCourse()?.courseId || "");
  const [assignmentFilter, setAssignmentFilter] = useState("");
  const [studentFilter, setStudentFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    let active = true;

    async function bootstrap() {
      try {
        const data = await APIService.GetUserPrimaryAttribute();
        const isManager = Boolean(data?.faculty_access);

        if (!isManager) {
          navigate("/", { replace: true });
          return;
        }

        if (active) {
          setAuthorized(true);
        }
      } catch (_error) {
        navigate("/", { replace: true });
      } finally {
        if (active) {
          setCheckingAuth(false);
        }
      }
    }

    bootstrap();
    return () => {
      active = false;
    };
  }, [navigate]);

  useEffect(() => {
    if (!authorized) {
      return;
    }

    let active = true;

    async function loadInsights() {
      try {
        setLoading(true);
        const data = await APIService.GetFacultyInsights({
          course: courseFilter,
          assignment: assignmentFilter,
          student: studentFilter,
          category: categoryFilter,
          dateFrom,
          dateTo,
        });
        if (!active) return;
        setPayload(data);
        setError("");
      } catch (loadError) {
        if (!active) return;
        if (loadError?.status === 401 || loadError?.status === 403) {
          navigate("/", { replace: true });
          return;
        }
        setError(loadError.message || "Failed to load faculty insights.");
      } finally {
        if (active) setLoading(false);
      }
    }

    loadInsights();
    return () => {
      active = false;
    };
  }, [assignmentFilter, authorized, categoryFilter, courseFilter, dateFrom, dateTo, navigate, studentFilter]);

  useEffect(() => {
    if (!authorized) {
      return;
    }

    const handleVisibilityRefresh = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      setLoading(true);
      APIService.GetFacultyInsights({
        course: courseFilter,
        assignment: assignmentFilter,
        student: studentFilter,
        category: categoryFilter,
        dateFrom,
        dateTo,
      })
        .then((data) => {
          setPayload(data);
          setError("");
        })
        .catch((loadError) => {
          if (loadError?.status === 401 || loadError?.status === 403) {
            navigate("/", { replace: true });
            return;
          }
          setError(loadError.message || "Failed to load faculty insights.");
        })
        .finally(() => setLoading(false));
    };

    window.addEventListener("focus", handleVisibilityRefresh);
    document.addEventListener("visibilitychange", handleVisibilityRefresh);
    return () => {
      window.removeEventListener("focus", handleVisibilityRefresh);
      document.removeEventListener("visibilitychange", handleVisibilityRefresh);
    };
  }, [assignmentFilter, authorized, categoryFilter, courseFilter, dateFrom, dateTo, navigate, studentFilter]);

  const sessions = useMemo(() => payload?.sessions || [], [payload]);
  const summary = useMemo(() => payload?.summary || {}, [payload]);

  const filteredSessions = sessions;

  const usageTrend = useMemo(() => summary.usage_over_time || [], [summary]);
  const courseTrend = useMemo(() => {
    const counts = new Map();
    filteredSessions.forEach((session) => {
      if (!session.courseCovered) return;
      counts.set(session.courseCovered, (counts.get(session.courseCovered) || 0) + 1);
    });
    return Array.from(counts.entries())
      .sort((left, right) => right[1] - left[1])
      .slice(0, 6);
  }, [filteredSessions]);
  const assignmentTrend = useMemo(() => {
    const counts = new Map();
    filteredSessions.forEach((session) => {
      const label = session.assignmentName || "Course-wide context";
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    return Array.from(counts.entries())
      .sort((left, right) => right[1] - left[1])
      .slice(0, 6);
  }, [filteredSessions]);

  const courses = [...new Set(sessions.map((session) => session.courseCovered).filter(Boolean))];
  const assignments = [...new Set(
    sessions
      .filter((session) => !courseFilter || session.courseCovered === courseFilter)
      .map((session) => session.assignmentName)
      .filter(Boolean)
  )];
  const studentOptions = useMemo(() => {
    const map = new Map();
    sessions.forEach((session) => {
      if (!session.studentUniqueId) return;
      map.set(session.studentUniqueId, session.studentDisplayName || session.studentUniqueId);
    });
    return Array.from(map.entries()).sort((left, right) => left[1].localeCompare(right[1]));
  }, [sessions]);
  const categories = [...new Set(sessions.flatMap((session) => session.questionCategories || []).filter(Boolean))];
  const workspaceCourse = useMemo(() => {
    if (!courseFilter) {
      return getFacultyWorkspaceCourse();
    }
    return { courseId: courseFilter, name: "" };
  }, [courseFilter]);

  useEffect(() => {
    setFacultyWorkspaceCourse(workspaceCourse);
  }, [workspaceCourse]);

  if (checkingAuth || !authorized) {
    return null;
  }

  return (
    <FacultyDashboardShell
      title="Insights"
      description="See high-level usage patterns across courses and assignments."
      error={error}
      loading={loading}
      loadingLabel="Loading insights…"
      workspaceCourse={workspaceCourse}
      workspaceMode={courseFilter ? "selected_course" : "all_courses"}
      workspaceHint={
        courseFilter
          ? "This section is focused on insight trends for the selected course."
          : "Use the filters to narrow insights to a single course, assignment, or student when needed."
      }
      nextActions={
        courseFilter
          ? [
              { to: "/dashboard", label: "My Courses" },
              { to: "/faculty/uploads", label: "Content" },
              { to: "/faculty/review", label: "Sessions" },
            ]
          : [{ to: "/dashboard", label: "My Courses" }]
      }
    >
          {!loading && !error && (
            <>
              <div className="faculty-stats-grid">
                <div className="faculty-stat-card">
                  <span>Total Sessions</span>
                  <strong>{summary.total_sessions ?? 0}</strong>
                </div>
                <div className="faculty-stat-card">
                  <span>Active Students</span>
                  <strong>{summary.active_students ?? 0}</strong>
                </div>
                <div className="faculty-stat-card">
                  <span>Most Active Course</span>
                  <strong>{summary.most_active_course || "—"}</strong>
                </div>
                <div className="faculty-stat-card">
                  <span>Most Active Assignment</span>
                  <strong>{summary.most_active_assignment || "—"}</strong>
                </div>
                <div className="faculty-stat-card">
                  <span>Recent Activity</span>
                  <strong>{summary.latest_session_at ? formatLocalDateTime(summary.latest_session_at) : "—"}</strong>
                </div>
              </div>

              <main id="main-content" className="faculty-content-grid insights-grid">
                <div className="faculty-card">
                  <h2>Filters</h2>
                  <div className="faculty-filter-grid">
                    <div className="session-filter-field">
                      <label className="session-filter-label" htmlFor="insights-course-filter">
                        Course
                      </label>
                      <select
                        id="insights-course-filter"
                        className="history-input"
                        value={courseFilter}
                        onChange={(e) => { setCourseFilter(e.target.value); setAssignmentFilter(""); }}
                        aria-label="Filter insights by course"
                      >
                        <option value="">All courses</option>
                        {courses.map((course) => <option key={course} value={course}>{course}</option>)}
                      </select>
                    </div>

                    <div className="session-filter-field">
                      <label className="session-filter-label" htmlFor="insights-assignment-filter">
                        Assignment or topic
                      </label>
                      <select
                        id="insights-assignment-filter"
                        className="history-input"
                        value={assignmentFilter}
                        onChange={(e) => setAssignmentFilter(e.target.value)}
                        aria-label="Filter insights by assignment or topic"
                      >
                        <option value="">All assignments / topics</option>
                        {assignments.map((assignment) => <option key={assignment} value={assignment}>{assignment}</option>)}
                      </select>
                    </div>

                    <div className="session-filter-field">
                      <label className="session-filter-label" htmlFor="insights-student-filter">
                        Student
                      </label>
                      <select
                        id="insights-student-filter"
                        className="history-input"
                        value={studentFilter}
                        onChange={(e) => setStudentFilter(e.target.value)}
                        aria-label="Filter insights by student"
                      >
                        <option value="">All students</option>
                        {studentOptions.map(([studentId, label]) => <option key={studentId} value={studentId}>{label}</option>)}
                      </select>
                    </div>

                    <div className="session-filter-field">
                      <label className="session-filter-label" htmlFor="insights-category-filter">
                        Question category
                      </label>
                      <select
                        id="insights-category-filter"
                        className="history-input"
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        aria-label="Filter insights by question category"
                      >
                        <option value="">All question categories</option>
                        {categories.map((category) => <option key={category} value={category}>{category}</option>)}
                      </select>
                    </div>

                    <div className="session-filter-field session-filter-date-group">
                      <div className="session-filter-label">Session start date</div>
                      <div className="session-filter-date-grid">
                        <div className="session-filter-field compact">
                          <label className="session-filter-sublabel" htmlFor="insights-date-from">
                            Start date
                          </label>
                          <input
                            id="insights-date-from"
                            className="history-input"
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            aria-label="Filter insights by session start date from"
                          />
                        </div>
                        <div className="session-filter-field compact">
                          <label className="session-filter-sublabel" htmlFor="insights-date-to">
                            End date
                          </label>
                          <input
                            id="insights-date-to"
                            className="history-input"
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            aria-label="Filter insights by session start date to"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="faculty-card">
                  <h2>Sessions By Date</h2>
                  <div className="faculty-list">
                    {usageTrend.map((item) => (
                      <div key={item.date} className="faculty-list-item">
                        <span>{item.date}</span>
                        <strong>{item.count}</strong>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="faculty-card">
                  <h2>Sessions By Course</h2>
                  <div className="faculty-list">
                    {courseTrend.map(([label, count]) => (
                      <div key={label} className="faculty-list-item">
                        <span>{label}</span>
                        <strong>{count}</strong>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="faculty-card">
                  <h2>Sessions By Assignment</h2>
                  <div className="faculty-list">
                    {assignmentTrend.map(([label, count]) => (
                      <div key={label} className="faculty-list-item">
                        <span>{label}</span>
                        <strong>{count}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              </main>

              <div className="faculty-card faculty-table-card">
                <div className="faculty-table-header">
                  <div>
                    <h2>Session Details</h2>
                    <p>{filteredSessions.length} filtered sessions</p>
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table table-bordered table-hover align-middle">
                    <thead>
                      <tr>
                        <th>Course</th>
                        <th>Assignment / Topic</th>
                        <th>Student</th>
                        <th>Started</th>
                        <th>Duration</th>
                        <th>Messages</th>
                        <th>Transcript Status</th>
                        <th>Categories</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSessions.length === 0 && (
                        <tr>
                          <td colSpan={8} className="text-center text-muted">No session data matches the current filters yet.</td>
                        </tr>
                      )}
                      {filteredSessions.map((session) => (
                        <tr key={session.sessionId}>
                          <td>{session.courseCovered}</td>
                          <td>{session.assignmentName || "Course-wide context"}</td>
                          <td>{session.studentDisplayName || session.studentUniqueId}</td>
                          <td>{formatLocalDateTime(session.startTime)}</td>
                          <td>{session.durationMinutes ?? "—"}</td>
                          <td>{session.messageCount}</td>
                          <td>{session.transcriptStatus || "unknown"}</td>
                          <td>{(session.questionCategories || []).join(", ") || "Uncategorized"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
    </FacultyDashboardShell>
  );
}

export default FacultyInsights;
